/**
 * Sets up defaults and analytics for fancybox modals & galleries.
 *
 * Helpers:
 *   Add attribute `data-fancybox`` to open a link in a modal.
 *
 * Analytics events:
 *   Modal Opened|Closed
 *   Modal Fullscreen Enter|Exit
 *   Modal Thumbnails Show|Hide
 *   Modal Slideshow Play|Pause
 *   Modal Slide Change
 *   Modal Download
 *   Modal Zoom Toggle
 *   Modal Share Dialog Opened|Closed
 *   Modal Share Clicked (data.network will contain facebook|pinterest|twitter)
 *
 * @since 0.1.0
 */

import {$body, $document} from './references.js';
import * as analytics from './analytics.js';
import * as youtube from './youtube.js';

// console.log('Site:modal');

let wasShareDialogOpen = false;


// Set global defaults
jQuery.extend(jQuery.fancybox.defaults, {
  loop    : true,
  buttons : [
    'slideShow',
    'fullScreen',
    'thumbs',
    'share',
    'download',
    'zoom',
    'close',
  ],
  idleTime          : 6,
  animationDuration : 300,

  // Note: Mouse wheel support added in 2.5.8, npm hasn't been updated yet

  // If changing any thumbs options, need to provide the entire object or set individually below
  // thumbs : {
  //   autoStart   : true,                   // Display thumbnails on opening
  //   hideOnClose : true,                   // Hide thumbnail grid when closing animation starts
  //   parentEl    : '.fancybox-container',  // Container is injected into this element
  //   axis        : 'x',                    // Vertical (y) or horizontal (x) scrolling. Need to setup CSS for other x orientation
  // },

  // 'close', 'next', 'nextOrClose', 'toggleControls', 'zoom', false (disable click handling)
  clickContent : false,

  // false, 'fade', 'slide', 'circular', 'tube', 'zoom-in-out', 'rotate'
  transitionEffect   : 'slide',
  transitionDuration : 400,
});

jQuery.fancybox.defaults.btnTpl.arrowLeft =
  '<button data-fancybox-prev class="fancybox-button fancybox-button--arrow_left" title="{{PREV}}">' +
    '<svg viewBox="0 0 153.9 291.7">' +
      '<path d="M153.9,8c0-2.1-0.8-4.1-2.3-5.7c-3.1-3.1-8.3-3.1-11.4,0L2.3,140.2c-3.1,3.1-3.1,8.3,0,11.4l137.8,137.8 c3.1,3.1,8.3,3.1,11.4,0c3.1-3.1,3.1-8.3,0-11.4L19.4,145.8L151.5,13.7C153.1,12.2,153.9,10.1,153.9,8L153.9,8z"></path>' +
    '</svg>' +
  '</button>';

jQuery.fancybox.defaults.btnTpl.arrowRight =
  '<button data-fancybox-next class="fancybox-button fancybox-button--arrow_right" title="{{NEXT}}">' +
    '<svg viewBox="0 0 153.9 291.7">' +
      '<path d="M0,8c0,2.1,0.8,4.2,2.4,5.7l132.1,132.1L2.4,278c-3.1,3.1-3.1,8.3,0,11.4c3.1,3.1,8.3,3.1,11.4,0l137.8-137.8 c3.1-3.1,3.1-8.3,0-11.4L13.7,2.3c-3.1-3.1-8.3-3.1-11.4,0C0.8,3.9,0,5.9,0,8L0,8z"></path>' +
    '</svg>' +
  '</button>';

$document.on('onInit.fb', onInit);


/**
 * Called when an instance is initialized.
 *
 * Used for analytics.
 *
 * @since 0.1.0
 *
 * @param  object  event    Event.
 * @param  object  instance Fancybox instance.
 */
function onInit(event, instance) {
  // console.log('Site:modal:onInit');
  // console.log(arguments);

  // Store the id of the closest element that opened this modal to be used for analytics identification
  // This is undefined if the modal was opened via a hash in the URL
  instance.dcClosestElementId = undefined; //eslint-disable-line

  if (instance.group[instance.currIndex].opts.$orig) {
    // This is the new way of getting the element that triggered the opening of fancybox
    instance.dcClosestElementId = instance.group[instance.currIndex].opts.$orig.closest('[id]').attr('id');
  }

  instance.$refs.container
    .on('onActivate.fb', onActivate)
    .on('beforeShow.fb', onBeforeShow)
    // For media types like images, which are cacheable, this is only called when first viewed
    // For other types like iframes, this is called on every view. Since iframes are destroyed when no
    // longer on the screen, all handlers need to be reinitialized on each view
    .on('afterLoad.fb', onAfterLoad)
    .on('beforeClose.fb', onBeforeClose)
    .on('onFullscreenChange.fb', onFullscreenChange)
    .on('onThumbsShow.fb onThumbsHide.fb', onThumbsChange)
  ;
  instance.$refs.toolbar.on('click', onToolBarClick);

  if (isShareDialog(instance)) {
    instance.$refs.stage.on('click', 'a', onShareButtonClicked);
  }
}


/**
 * Called when the instance is brought to the front.
 *
 * Used for analytics.
 *
 * @since 0.1.0
 *
 * @param  object  event    Event.
 * @param  object  instance Fancybox instance.
 */
function onActivate(event, instance) {
  // console.log('Site:modal:onActivate');
  // console.log(arguments);

  // Store the id of the closest element that opened this modal to be used for analytics identification
  // This is undefined if the modal was opened via a hash in the URL
  const $originalElement = instance.opts.$orig ? instance.opts.$orig : jQuery(document.activeElement);

  instance.dcClosestElementId = $originalElement.closest('[id]').attr('id');

  if (isShareDialog(instance)) {
    wasShareDialogOpen = true;

    track('Modal Share Dialog Opened');
  } else {
    if (wasShareDialogOpen) {
      // When the share dialog is closed, it reactivates the previous modal
      wasShareDialogOpen = false;
    } else {
      track('Modal Opened');
    }
  }
}


/**
 * Called before a slide is shown.  Used to apply cleanup when transitioning slides.
 *
 * Removes YouTube trackers when that slide is destroyed.
 *
 * @since 0.1.0
 *
 * @param  object  event    Event.
 * @param  object  instance Fancybox instance.
 * @param  object  slide    Slide.
 * @param  boolean firstRun Whether this is the first slide shown for this instance.
 */
function onBeforeShow(event, instance, slide, firstRun) {
  // console.log('Site:modal:onBeforeShow');
  // console.log(arguments);

  if (!firstRun) {
    track('Modal Slide Change');
  } else {
    $body.addClass('scrolling-disabled');
  }

  if (instance.dcVideoId) {
    // Last slide was a YouTube video, do teardown
    youtube.removeVideoFromList(instance.dcVideoId);

    instance.dcVideoId = undefined; // eslint-disable-line
  }
}


/**
 * Called after a slide's content has been loaded. Inspects the type of content
 * being loaded and adds tracking for youtube videos.
 *
 * Fancybox only keeps 3 slides in the DOM (except for iframe content). After switching
 * slides, the old ones are destroyed, so all handlers need to be re-added.
 *
 * @since 0.1.0
 *
 * @param  object event    Event.
 * @param  object instance Fancybox instance.
 * @param  object slide    Slide.
 */
function onAfterLoad(event, instance, slide) {
  // console.log('Site:modal:onAfterLoad');
  // console.log(arguments);

  if (slide.type !== 'iframe') {
    return;
  }

  const videoId = youtube.extractVideoId(slide.src);

  if (slide.src === videoId) {
    return;
  }

  // Add YouTube tracking
  const args = {
    events : {
      onReady       : youtube.onPlayerReady,
      onStateChange : youtube.onPlayerStateChange,
      onError       : youtube.onPlayerError,
    },
  };

  if (youtube.isAPIReady) {
    // console.log('Ready now');
    initPlayer();
  } else {
    // console.log('Wait until ready');
    $body.on('youTubePlayerAPIReady.Site', initPlayer);
  }

  function initPlayer() {
    // console.log('Site:modal:onAfterLoad:initPlayer');

    setTimeout(function () {
      instance.dcVideoId = videoId;

      youtube.addVideoToList(videoId, slide.$content.find('iframe').attr('id'), new YT.Player(slide.$content.find('iframe').attr('id'), args));
    });
  }
}


/**
 * Called before the instance is destroyed.  Used to apply cleanup.
 *
 * Removes YouTube trackers when that slide is destroyed.
 *
 * @since 0.1.0
 *
 * @param  object  event    Event.
 * @param  object  instance Fancybox instance.
 * @param  object  slide    Slide.
 */
function onBeforeClose(event, instance, slide) {
  // console.log('Site:modal:onBeforeClose');
  // console.log(arguments);

  if (isShareDialog(instance)) {
    track('Modal Share Dialog Closed');
  } else {
    track('Modal Closed');

    // TODO: Only remove this class if this is the last modal open
    $body.removeClass('scrolling-disabled');
  }

  if (instance.dcVideoId) {
    // Last slide was a YouTube video, do teardown
    youtube.removeVideoFromList(instance.dcVideoId);

    instance.dcVideoId = undefined; // eslint-disable-line
  }
}


/**
 * Called when the fullscreen mode changes.
 *
 * Used for analytics.
 *
 * @since 0.1.0
 *
 * @param  object  event        Event.
 * @param  object  instance     Fancybox instance.
 * @param  object  slide        Current slide.
 * @param  boolean isFullscreen Fullscreen status.
 */
function onFullscreenChange(event, instance, slide, isFullscreen) {
  // console.log('Site:modal:onFullscreenChange');
  // console.log(arguments);

  // Hide the Share button while fullscreen as it doesn't working correctly yet
  if (isFullscreen) {
    instance.$refs.toolbar.find('.fancybox-button--share').hide();
  } else {
    instance.$refs.toolbar.find('.fancybox-button--share').show();
  }

  track('Modal Fullscreen ' + (isFullscreen ? 'Enter' : 'Exit'));
}


/**
 * Called when the thumbs display mode changes.
 *
 * Used for analytics.
 *
 * @since 0.1.0
 *
 * @param  object  event        Event.
 * @param  object  instance     Fancybox instance.
 * @param  object  slide        Current slide.
 * @param  boolean isFullscreen Fullscreen status.
 */
function onThumbsChange(event, instance, slide, isFullscreen) {
  // console.log('Site:modal:onThumbsChange');
  // console.log(arguments);

  track('Modal Thumbnails ' + (event.type === 'onThumbsShow' ? 'Show' : 'Hide'));
}


/**
 * Called when anything on the toolbar is clicked.
 *
 * Used for analytics.
 * Note: These won't track these events if made via the API (it currently lacks proper events for these).
 *
 * @since 0.1.0
 *
 * @param  object  event Event.
 */
function onToolBarClick(event) {
  // console.log('Site:modal:onToolBarClick');
  // console.log(arguments);

  const $element = jQuery(event.target).closest('[class]')
      , classes  = $element.attr('class')
      , matches  = classes.match(/fancybox-button--(.+)$/)
      // , instance = jQuery.fancybox.getInstance()
  ;

  // console.log('$element', $element);
  // console.log('classes', classes);
  // console.log('matches', matches);

  if (!matches || matches.length !== 2) {
    return;
  }

  // console.log('matches[1]', matches[1]);

  switch (matches[1]) {
    case 'play':
      track('Modal Slideshow Pause');
      break;

    case 'pause':
      track('Modal Slideshow Play');
      break;

    case 'download':
      track('Modal Download', {url: $element.attr('href')});
      break;

    case 'zoom':
      track('Modal Zoom Toggle');
      break;
  }
}


/**
 * Called when one of the share buttons is clicked.
 *
 * Used for analytics.
 *
 * @since 0.1.0
 *
 * @param  object event Event.
 */
function onShareButtonClicked(event) {
  // console.log('Site:modal:onShareButtonClicked');
  // console.log(arguments);

  const href = jQuery(event.target).closest('[href]').attr('href');

  if (href.indexOf('/www.facebook.com/') !== -1) {
    track('Modal Share Clicked', {
      network : 'facebook',
    });
  } else if (href.indexOf('/www.pinterest.com/') !== -1) {
    track('Modal Share Clicked', {
      network : 'pinterest',
    });
  } else if (href.indexOf('/twitter.com/') !== -1) {
    track('Modal Share Clicked', {
      network : 'twitter',
    });
  }
}


/**
 * Determines if the provided instance is an instance of the share modal.
 *
 * @since 0.1.0
 *
 * @param  object  instance Fancybox instance.
 *
 * @returns boolean Whether this is a share modal.
 */
function isShareDialog(instance) {
  // console.log('Site:modal:isShareDialog');
  // console.log('instance', instance);

  if (
    instance.group &&
    instance.group.length &&
    instance.group[0].src &&
    typeof instance.group[0].src === 'string' &&
    instance.group[0].src.indexOf('class="fancybox-share"') !== -1
  ) {
    return true;
  }

  return false;
}


/**
 * Tracks a modal event, adding common data to it.
 *
 * @since 0.1.0
 *
 * @param  string eventName Name of the event (title case).
 * @param  object data      Additional data to be merged with the default data.
 */
function track(eventName, data) {
  // console.log('Site:modal:track');

  const instance  = jQuery.fancybox.getInstance();

  if (!instance) {
    return;
  }

  // console.log('eventName', eventName);
  // console.log('instance', instance);

  const eventData = {
    containerId : instance.dcClosestElementId,
    index       : instance.currIndex + 1,  // Start count at 1 instead of 0 for analytics
    count       : instance.group.length,
    progress    : instance.group.length > 1 ? instance.currIndex / (instance.group.length - 1) : 1,
    page        : siteData.page,
  };

  jQuery.extend(eventData, data);
  analytics.track(eventName, eventData);
}


/**
 * Opens the modal.
 *
 * @since 0.1.0
 *
 * @param {object} args Modal arguments.
 */
export function open(args) {
  // console.log('Site:modal:open');

  jQuery.fancybox.open(args);
}
